import { Fragment, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { apiErr, fmtMoney } from "@/lib/api";
import { PageHeader, Loader } from "@/components/ui-kit";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ValStatus, fmtDelay } from "@/lib/validation";
import { CheckCircle2, XCircle, RotateCcw, Ban, UserRoundCog, ExternalLink, Send } from "lucide-react";
import { toast } from "sonner";

const dt = (s) => (s ? new Date(s).toLocaleString("fr-FR") : "—");
const LABEL = { approve: "Approuver", reject: "Rejeter", correction: "Demander une correction", cancel: "Annuler la demande", resubmit: "Soumettre à nouveau" };

function SourceView({ src }) {
  const simple = Object.entries(src).filter(([k, v]) => !["id", "items", "lines", "payments", "attachments", "documents"].includes(k) && v !== "" && v != null && typeof v !== "object");
  const lines = src.items || src.lines || [];
  const atts = src.attachments || src.documents || [];
  return (
    <div className="space-y-3" data-testid="val-source">
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
        {simple.map(([k, v]) => <Fragment key={k}><dt className="text-slate-500">{k.replace(/_/g, " ")}</dt><dd className="break-words">{typeof v === "number" ? v.toLocaleString("fr-FR") : String(v)}</dd></Fragment>)}
      </dl>
      {lines.length > 0 && <table className="w-full text-xs border"><thead className="bg-slate-50"><tr>{["Désignation", "Qté", "PU", "Total"].map((h) => <th key={h} className="p-1 text-left">{h}</th>)}</tr></thead>
        <tbody>{lines.map((l, i) => <tr key={i} className="border-t"><td className="p-1">{l.designation || l.article_name || l.description}</td><td className="p-1">{l.quantite}</td><td className="p-1">{l.prix_unitaire ?? ""}</td><td className="p-1">{l.total ?? ""}</td></tr>)}</tbody></table>}
      {atts.length > 0 && <p className="text-xs text-slate-500">Pièces jointes : {atts.map((a) => a.nom || a.name || a.filename).join(", ")}</p>}
    </div>
  );
}

function ActionDialog({ action, onClose, onConfirm, assignees }) {
  const [comment, setComment] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [users, setUsers] = useState([]);
  useEffect(() => { if (action === "reassign") api.get("/users").then((r) => setUsers(r.data.filter((u) => u.active !== false))); }, [action]);
  const mandatory = ["reject", "correction", "cancel"].includes(action);
  return (
    <Dialog open={!!action} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="val-action-dialog">
        <DialogHeader><DialogTitle className="font-head">{action === "reassign" ? "Réaffecter un validateur" : LABEL[action]}</DialogTitle></DialogHeader>
        {action === "reassign" && <div className="grid grid-cols-2 gap-2">
          <Select value={from} onValueChange={setFrom}><SelectTrigger data-testid="val-reassign-from"><SelectValue placeholder="Remplacer" /></SelectTrigger><SelectContent>{assignees.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent></Select>
          <Select value={to} onValueChange={setTo}><SelectTrigger data-testid="val-reassign-to"><SelectValue placeholder="Par" /></SelectTrigger><SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent></Select>
        </div>}
        <Textarea rows={3} placeholder={mandatory ? "Motif / commentaire (obligatoire)" : "Commentaire (facultatif)"} value={comment} onChange={(e) => setComment(e.target.value)} data-testid="val-action-comment" />
        <Button disabled={(mandatory && !comment.trim()) || (action === "reassign" && (!from || !to))} onClick={() => onConfirm({ comment, from_id: from, to_id: to })} className="bg-brand-600 hover:bg-brand-700 text-white" data-testid="val-action-confirm">Confirmer</Button>
      </DialogContent>
    </Dialog>
  );
}

function Steps({ r }) {
  return (
    <ol className="space-y-2" data-testid="val-steps">
      {r.steps.map((s, i) => {
        const decs = r.decisions.filter((d) => d.step === i && d.round === r.round);
        const state = r.statut === "Approuvée" || i < r.current_step ? "done" : i === r.current_step && !["Rejetée", "Annulée"].includes(r.statut) ? "current" : "todo";
        return (
          <li key={i} className={`rounded-lg border p-3 ${state === "current" ? "border-brand-500 bg-brand-50/40" : ""}`}>
            <p className="text-sm font-semibold">{i + 1}. {s.nom} <span className="text-xs font-normal text-slate-500">({s.mode === "un" ? "un seul validateur suffit" : "tous doivent approuver"})</span></p>
            <div className="flex flex-wrap gap-1.5 mt-1">{s.assignees.map((a) => { const d = decs.find((x) => x.by_id === a.id); return <span key={a.id} className={`text-xs rounded-full px-2 py-0.5 ${d ? (d.action === "approve" ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800") : "bg-slate-100"}`}>{a.name}{d ? ` · ${d.action === "approve" ? "approuvé" : d.action === "reject" ? "rejeté" : "correction"}` : ""}</span>; })}</div>
          </li>
        );
      })}
    </ol>
  );
}

export default function ValidationDetail() {
  const { id } = useParams();
  const [r, setR] = useState(null);
  const [action, setAction] = useState(null);
  const load = () => api.get(`/validations/requests/${id}`).then((x) => setR(x.data)).catch((e) => { toast.error(apiErr(e)); setR(false); });
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (r === null) return <Loader />;
  if (r === false) return <p className="text-sm text-rose-600" data-testid="val-detail-error">Demande inaccessible.</p>;
  const run = async ({ comment, from_id, to_id }) => {
    try {
      let x;
      if (action === "cancel") x = await api.post(`/validations/requests/${id}/cancel`, { comment });
      else if (action === "reassign") x = await api.post(`/validations/requests/${id}/reassign`, { from_id, to_id, comment });
      else if (action === "resubmit") { await api.post("/validations/submit", { op_type: r.op_type, source_id: r.source_id, comment }); x = await api.get(`/validations/requests/${id}`); }
      else x = await api.post(`/validations/requests/${id}/decision`, { action, comment });
      setR(x.data); setAction(null); toast.success("Action enregistrée");
    } catch (e) { toast.error(apiErr(e)); }
  };
  const btn = (k, Icon, cls) => r.can[k] && <Button key={k} onClick={() => setAction(k)} className={cls} data-testid={`val-btn-${k}`}><Icon className="h-4 w-4 mr-1.5" />{k === "reassign" ? "Réaffecter" : LABEL[k]}</Button>;
  return (
    <div data-testid="validation-detail">
      <PageHeader title={`${r.numero} — ${r.op_label} ${r.source_ref}`} subtitle={`${r.module} · circuit « ${r.circuit_nom} » v${r.circuit_version} · tour ${r.round}`}>
        <ValStatus s={r.statut} testid="val-detail-status" />
      </PageHeader>
      <div className="flex flex-wrap gap-2 mb-4">
        {btn("approve", CheckCircle2, "bg-emerald-600 hover:bg-emerald-700 text-white")}
        {btn("correction", RotateCcw, "bg-orange-500 hover:bg-orange-600 text-white")}
        {btn("reject", XCircle, "bg-rose-600 hover:bg-rose-700 text-white")}
        {btn("resubmit", Send, "bg-brand-600 hover:bg-brand-700 text-white")}
        {btn("reassign", UserRoundCog, "bg-white border text-slate-700 hover:bg-slate-50")}
        {btn("cancel", Ban, "bg-white border text-slate-700 hover:bg-slate-50")}
        <Link to={r.link} className="inline-flex items-center text-sm text-brand-700 hover:underline ml-auto"><ExternalLink className="h-4 w-4 mr-1" />Ouvrir le module d'origine</Link>
      </div>
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <div className="rounded-xl border bg-white p-4">
            <div className="grid sm:grid-cols-4 gap-3 text-sm mb-3">
              <div><p className="text-xs text-slate-500">Demandeur</p><p className="font-medium">{r.demandeur_name}</p></div>
              <div><p className="text-xs text-slate-500">Montant</p><p className="font-mono font-semibold" data-testid="val-detail-amount">{r.montant != null ? fmtMoney(r.montant) : "—"}</p></div>
              <div><p className="text-xs text-slate-500">Soumise le</p><p>{dt(r.created_at)}</p></div>
              <div><p className="text-xs text-slate-500">Délai écoulé</p><p>{fmtDelay(r.elapsed_h)}</p></div>
            </div>
            <p className="text-sm"><span className="text-slate-500">Objet : </span>{r.objet}</p>
          </div>
          <div className="rounded-xl border bg-white p-4"><h3 className="font-head font-semibold mb-2">Dossier source</h3>{r.source_missing ? <p className="text-sm text-rose-600">L'opération d'origine a été supprimée.</p> : <SourceView src={r.source} />}</div>
        </div>
        <div className="space-y-4">
          <div className="rounded-xl border bg-white p-4"><h3 className="font-head font-semibold mb-2">Circuit</h3><Steps r={r} /></div>
          <div className="rounded-xl border bg-white p-4"><h3 className="font-head font-semibold mb-2">Historique</h3>
            <ul className="space-y-2 text-xs" data-testid="val-history">{[...r.history].reverse().map((h, i) => <li key={i} className="border-l-2 border-brand-200 pl-2"><p className="font-medium">{h.action}</p><p className="text-slate-500">{dt(h.at)} · {h.by}</p>{h.note && <p className="italic text-slate-600">« {h.note} »</p>}</li>)}</ul>
          </div>
        </div>
      </div>
      <ActionDialog action={action} onClose={() => setAction(null)} onConfirm={run} assignees={r.steps[r.current_step]?.assignees || []} />
    </div>
  );
}
